<script>
import Layout from "../../../layouts/main";
import PageHeader from "@/components/Page-header";
import copasstQuarterlyReportAPI from "@/services/standards/copasst/copasstQuarterlyReportAPI";
import newScoresAPI from "@/services/newScores/newScoresAPI";
import Swal from "sweetalert2";
import VersionNote from "@/components/document/VersionNote.vue";

function currentQuarter() {
  return Math.floor(new Date().getMonth() / 3) + 1;
}

/**
 * 1.1.6 — Editor del informe trimestral de gestión del COPASST / Vigía de SST.
 * Selector año/trimestre con precarga desde los módulos existentes (reuniones del seguimiento,
 * conformación vigente, inspecciones, condiciones inseguras, capacitaciones y accidentalidad).
 * Los indicadores quedan editables porque el comité puede reportar gestión no registrada en la
 * plataforma. Genera el PDF branded (SST-FT-033) con líneas de firma en blanco.
 */
export default {
  page: { title: "Informe trimestral — COPASST / Vigía de SST" },
  components: { VersionNote, Layout, PageHeader },
  data() {
    return {
      // Texto que queda en el control de versiones del PDF
      versionNote: "",
      title: "1.1.6 Informe trimestral de gestión del COPASST / Vigía de SST",
      items: [
        { text: "Estándares", href: "/forms/estandares" },
        { text: "Informe trimestral", active: true },
      ],
      companyID: this.$store.getters["company/getCompany"].id || null,
      reportId: this.$route.params.reportId || null,
      years: [],
      trimestres: [
        { v: 1, t: "I — Enero a Marzo" },
        { v: 2, t: "II — Abril a Junio" },
        { v: 3, t: "III — Julio a Septiembre" },
        { v: 4, t: "IV — Octubre a Diciembre" },
      ],
      form: {
        year: new Date().getFullYear(),
        quarter: currentQuarter(),
        tipo_comite: "copasst",
        fecha: new Date().toISOString().substring(0, 10),
        responsable: "",
        miembros: [],
        reuniones_ordinarias: 0,
        reuniones_extraordinarias: 0,
        inspecciones_realizadas: 0,
        condiciones_inseguras_reportadas: 0,
        capacitaciones_realizadas: 0,
        accidentes_reportados: 0,
        accidentes_investigados: 0,
        actividades: "",
        recomendaciones: "",
        presidente_nombre: "",
        secretario_nombre: "",
        gerencia_nombre: "",
      },
      submitting: false,
      loading: true,
    };
  },
  computed: {
    esVigia() {
      return this.form.tipo_comite === "vigia";
    },
    organoNombre() {
      return this.esVigia ? "Vigía de SST" : "COPASST";
    },
    reunionesTotal() {
      return (this.form.reuniones_ordinarias || 0) + (this.form.reuniones_extraordinarias || 0);
    },
  },
  watch: {
    "form.year"() { this.autoPrefill(); },
    "form.quarter"() { this.autoPrefill(); },
  },
  methods: {
    rangeYear() {
      for (let i = 2019; i <= 2035; i++) this.years.push(i);
    },
    autoPrefill() {
      if (!this.reportId) this.prefill(false);
    },
    /**
     * Trae los datos del trimestre desde los módulos existentes.
     * force=true sobrescribe lo digitado; force=false solo rellena lo que está vacío o en cero.
     */
    prefill(force = false) {
      if (!this.form.year || !this.form.quarter) return;
      copasstQuarterlyReportAPI.suggest(this.companyID, this.form.year, this.form.quarter)
        .then((res) => {
          if (!res) return;

          // El tipo de órgano (COPASST o Vigía) lo define el nº de trabajadores activos.
          if (res.tipo_comite && (force || !this.reportId)) this.form.tipo_comite = res.tipo_comite;

          const setNum = (campo, valor) => {
            if (force || !this.form[campo]) this.form[campo] = valor || 0;
          };
          if (force || (!this.form.reuniones_ordinarias && !this.form.reuniones_extraordinarias)) {
            this.form.reuniones_ordinarias = res.reuniones_total || 0;
          }
          setNum("inspecciones_realizadas", res.inspecciones_realizadas);
          setNum("condiciones_inseguras_reportadas", res.condiciones_inseguras_reportadas);
          setNum("capacitaciones_realizadas", res.capacitaciones_realizadas);
          setNum("accidentes_reportados", res.accidentes_reportados);
          setNum("accidentes_investigados", res.accidentes_investigados);

          if (res.miembros && res.miembros.length && (force || !this.form.miembros.length)) {
            this.form.miembros = res.miembros.map((m) => ({ nombre: m.nombre, rol: m.rol }));
          }
          if (res.presidente_nombre && (force || !this.form.presidente_nombre)) {
            this.form.presidente_nombre = res.presidente_nombre;
          }
          if (res.secretario_nombre && (force || !this.form.secretario_nombre)) {
            this.form.secretario_nombre = res.secretario_nombre;
          }
          if (res.actividades_sugerida && (force || !this.form.actividades)) {
            this.form.actividades = res.actividades_sugerida;
          }

          if (force) {
            Swal.fire(
              "Datos del trimestre",
              `Se encontraron ${res.reuniones_total || 0} reunión(es), ` +
              `${res.inspecciones_realizadas || 0} inspección(es), ` +
              `${res.capacitaciones_realizadas || 0} capacitación(es) y ` +
              `${res.accidentes_reportados || 0} accidente(s) registrados en la plataforma.`,
              "info"
            );
          }
        })
        .catch((e) => console.log(e));
    },
    agregarMiembro() {
      this.form.miembros.push({ nombre: "", rol: "" });
    },
    quitarMiembro(i) {
      this.form.miembros.splice(i, 1);
    },
    load() {
      if (!this.reportId) { this.loading = false; return; }
      copasstQuarterlyReportAPI.show(this.reportId)
        .then((r) => {
          if (r) {
            Object.keys(this.form).forEach((k) => {
              if (r[k] !== undefined && r[k] !== null) this.form[k] = r[k];
            });
            if (r.fecha) this.form.fecha = r.fecha.substring(0, 10);
            if (!Array.isArray(this.form.miembros)) this.form.miembros = [];
          }
          this.loading = false;
        })
        .catch(() => { this.loading = false; });
    },
    save() {
      this.submitting = true;
      const payload = { companies_id: this.companyID, ...this.form };
      payload.version_note = this.versionNote;
      const req = this.reportId
        ? copasstQuarterlyReportAPI.update(this.reportId, payload)
        : copasstQuarterlyReportAPI.save(payload);
      req.then((res) => {
        // El informe trimestral es evidencia del ítem 1.1.6 → marca pendiente.
        newScoresAPI.calculateScores({ companies_id: this.companyID, item_id: 6, status: "Pendiente" }).catch(() => {});
        this.submitting = false;
        if (!this.reportId && res && res.id) {
          this.reportId = res.id;
          this.$router.replace({ path: `/forms/estandares/informe-trimestral-copasst-editar/${res.id}` });
        }
        Swal.fire("¡Guardado!", "El informe trimestral se guardó correctamente.", "success");
      }).catch((e) => {
        this.submitting = false;
        Swal.fire("Error", "No se pudo guardar el informe.", "error");
        console.log(e);
      });
    },
    descargarPdf() {
      if (!this.reportId) {
        Swal.fire("Atención", "Guarda el informe antes de descargar el PDF.", "info");
        return;
      }
      window.open(copasstQuarterlyReportAPI.pdfUrl(this.reportId));
    },
    goBack() {
      this.$router.go(-1);
    },
  },
  mounted() {
    this.rangeYear();
    this.load();
    if (!this.reportId) this.prefill(false);
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="row animated animatedFadeInUp fadeInUp">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div v-if="loading" class="text-center py-4"><b-spinner variant="primary" /></div>

            <form v-else @submit.prevent="save">
              <!-- 1. Datos generales -->
              <h5 class="text-primary">1. Datos generales</h5>
              <div class="form-row">
                <b-form-group class="col-md-3" label="Año">
                  <select class="form-control" v-model.number="form.year" required>
                    <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
                  </select>
                </b-form-group>
                <b-form-group class="col-md-3" label="Trimestre">
                  <select class="form-control" v-model.number="form.quarter" required>
                    <option v-for="t in trimestres" :key="t.v" :value="t.v">{{ t.t }}</option>
                  </select>
                </b-form-group>
                <b-form-group class="col-md-3" label="Fecha del informe">
                  <b-form-input type="date" v-model="form.fecha" />
                </b-form-group>
                <b-form-group class="col-md-3" label="Responsable de la elaboración">
                  <b-form-input v-model="form.responsable" maxlength="150" />
                </b-form-group>
              </div>
              <div class="form-row">
                <!-- El tipo se propone según el nº de trabajadores activos, pero es corregible. -->
                <b-form-group class="col-md-4" label="Órgano que reporta">
                  <select class="form-control" v-model="form.tipo_comite">
                    <option value="copasst">COPASST (10 o más trabajadores)</option>
                    <option value="vigia">Vigía de SST (menos de 10 trabajadores)</option>
                  </select>
                </b-form-group>
                <div class="col-md-8 d-flex align-items-center">
                  <button type="button" class="btn btn-outline-primary" @click="prefill(true)">
                    <i class="mdi mdi-download"></i> Traer datos del trimestre
                  </button>
                  <small class="text-muted ml-2">
                    Trae reuniones, conformación, inspecciones, condiciones inseguras,
                    capacitaciones y accidentalidad ya registrados en la plataforma.
                  </small>
                </div>
              </div>

              <!-- 2. Conformación vigente -->
              <div class="d-flex align-items-center mt-2">
                <h5 class="text-primary mb-0 mr-3">2. Conformación vigente del {{ organoNombre }}</h5>
                <button type="button" class="btn btn-outline-primary btn-sm" @click="agregarMiembro">
                  <i class="mdi mdi-plus"></i> Agregar integrante
                </button>
              </div>
              <div class="table-responsive mt-2">
                <table class="table table-sm table-bordered mb-0">
                  <thead>
                    <tr>
                      <th style="width:40%">Nombre</th>
                      <th>{{ esVigia ? "Designación" : "Rol dentro del COPASST" }}</th>
                      <th style="width:60px"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(m, i) in form.miembros" :key="i">
                      <td><b-form-input v-model="m.nombre" maxlength="150" /></td>
                      <td><b-form-input v-model="m.rol" maxlength="150" /></td>
                      <td class="text-center align-middle">
                        <button type="button" class="btn btn-sm btn-outline-danger" @click="quitarMiembro(i)">
                          <i class="mdi mdi-delete"></i>
                        </button>
                      </td>
                    </tr>
                    <tr v-if="!form.miembros.length">
                      <td colspan="3" class="text-center text-muted">
                        Sin integrantes. Usa "Traer datos del trimestre" o agrégalos manualmente.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- 3. Reuniones -->
              <h5 class="text-primary mt-3">3. Reuniones realizadas en el trimestre</h5>
              <div class="form-row">
                <b-form-group class="col-md-4" label="Reuniones ordinarias">
                  <b-form-input type="number" min="0" v-model.number="form.reuniones_ordinarias" />
                </b-form-group>
                <b-form-group class="col-md-4" label="Reuniones extraordinarias">
                  <b-form-input type="number" min="0" v-model.number="form.reuniones_extraordinarias" />
                </b-form-group>
                <b-form-group class="col-md-4" label="Total (automático)">
                  <b-form-input :value="reunionesTotal" disabled />
                </b-form-group>
              </div>

              <!-- 4. Gestión del trimestre -->
              <h5 class="text-primary mt-2">4. Gestión del trimestre</h5>
              <div class="form-row">
                <b-form-group class="col-md-4" label="Inspecciones de seguridad realizadas">
                  <b-form-input type="number" min="0" v-model.number="form.inspecciones_realizadas" />
                </b-form-group>
                <b-form-group class="col-md-4" label="Reportes de condiciones inseguras atendidos">
                  <b-form-input type="number" min="0" v-model.number="form.condiciones_inseguras_reportadas" />
                </b-form-group>
                <b-form-group class="col-md-4" label="Capacitaciones y sensibilizaciones">
                  <b-form-input type="number" min="0" v-model.number="form.capacitaciones_realizadas" />
                </b-form-group>
              </div>

              <!-- 5. Accidentalidad -->
              <h5 class="text-primary mt-2">5. Accidentalidad del trimestre</h5>
              <div class="form-row">
                <b-form-group class="col-md-6" label="Accidentes de trabajo reportados">
                  <b-form-input type="number" min="0" v-model.number="form.accidentes_reportados" />
                </b-form-group>
                <b-form-group class="col-md-6" label="Accidentes con investigación realizada">
                  <b-form-input type="number" min="0" v-model.number="form.accidentes_investigados" />
                </b-form-group>
              </div>

              <!-- 6. Actividades -->
              <h5 class="text-primary mt-2">6. Actividades realizadas</h5>
              <b-form-textarea v-model="form.actividades" rows="5"
                placeholder="Actividades del comité durante el trimestre..." />

              <!-- 7. Recomendaciones -->
              <h5 class="text-primary mt-3">7. Recomendaciones a la gerencia</h5>
              <b-form-textarea v-model="form.recomendaciones" rows="3"
                placeholder="Recomendaciones a la gerencia..." />

              <!-- Firmas (líneas en blanco en el PDF) -->
              <h5 class="text-primary mt-3">Firmas (se firman a mano en el PDF)</h5>
              <div class="form-row">
                <b-form-group :class="esVigia ? 'col-md-6' : 'col-md-4'"
                  :label="esVigia ? 'Vigía de Seguridad y Salud en el Trabajo' : 'Presidente del COPASST'">
                  <b-form-input v-model="form.presidente_nombre" maxlength="150" />
                </b-form-group>
                <b-form-group v-if="!esVigia" class="col-md-4" label="Secretario del COPASST">
                  <b-form-input v-model="form.secretario_nombre" maxlength="150" />
                </b-form-group>
                <b-form-group :class="esVigia ? 'col-md-6' : 'col-md-4'"
                  label="Representante de la Gerencia (recibe)">
                  <b-form-input v-model="form.gerencia_nombre" maxlength="150" />
                </b-form-group>
              </div>

              <VersionNote v-model="versionNote" :editing="!!reportId" />

              <div class="text-right mt-3">
                <button type="button" class="btn btn-secondary mr-1" @click="goBack">Cancelar</button>
                <button type="button" class="btn btn-outline-primary mr-1" @click="descargarPdf" :disabled="!reportId">
                  <i class="mdi mdi-file-pdf-box"></i> Descargar PDF
                </button>
                <button type="submit" class="btn btn-primary" :disabled="submitting">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
